import {
  Box,
  Button,
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogProps,
  FormLabel,
  InputGroup,
  InputMessage,
  InputText,
  Stack,
  Text,
} from "@aksara-ui/react";
import { InputSelect } from "components/Form/InputSelect";
import { Field, FieldProps, Form, Formik, FormikValues } from "formik";
import { getRole } from "hooks";
import React, { useRef, useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { storeGeneralSetting, updateGeneralSetting } from "redux/actions";
import styled from "styled-components";
import * as yup from "yup";

const FormWrapper = styled(Box)`
  display: flex;
  flex-direction: column;
  gap: 12px;
`;

const GridWrapper = styled(Box)`
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 12px;
`;

interface Props {
  generalSettingId: string;
}

interface ValueSchema {
  label: string;
  value: string | number;
}

const initialValues = {
  name: "",
  value: "",
  category: "",
  description: "",
  tenantId: "",
};

const GeneralSettingFormModal: React.FC<Props & DialogProps> = ({
  isOpen,
  onClose,
  generalSettingId,
}) => {
  const isAddMode = !generalSettingId;
  const dispatch = useDispatch();
  const userRole = getRole();
  const formikRef = useRef<FormikValues>() as any;

  const [tenant, setTenant] = useState<ValueSchema | null>(null);

  const { isFormLoading, generalSettings } = useSelector(
    (state: any) => state.generalSettingReducer
  );

  const [tenantItems] = React.useState([
   { label: "Ancol Points", value: 3 },
  ]);

  const handleCloseModal = () => {
    onClose && onClose();
    setTenant(null);
  };

  const handleSubmit = (values: any) => {
    const { name, value, category, description, tenantId } = values;

    isAddMode
      ? dispatch(
          storeGeneralSetting(
            { name, value, category, description, tenantId },
            handleCloseModal
          ) as any
        )
      : dispatch(
          updateGeneralSetting(
            generalSettingId,
            { name, value, category, description, tenantId },
            handleCloseModal
          ) as any
        );
  };

  useEffect(() => {
    if (!isAddMode) {
      const generalSettingDetail = generalSettings.find(
        (generalSetting: any) =>
          generalSetting.id === parseInt(generalSettingId)
      );

      if (formikRef.current) {
        formikRef.current.setFieldValue("name", generalSettingDetail.name);
        formikRef.current.setFieldValue("value", generalSettingDetail.value);
        formikRef.current.setFieldValue(
          "category",
          generalSettingDetail.category
        );
        formikRef.current.setFieldValue(
          "description",
          generalSettingDetail.description
        );
        formikRef.current.setFieldValue(
          "tenantId",
          generalSettingDetail.tenant_id
        );

        tenantItems.map((item) =>
          item.value.toString() === generalSettingDetail.tenant_id
            ? setTenant(item)
            : null
        );
      }
    }
  }, [isAddMode, generalSettings, generalSettingId]);

  const validationSchema = yup.object({
    name: yup.string().required("Name is required field"),
    value: yup.string().required("Value is required field"),
    category: yup.string().required("Category is required field"),
    description: yup.string().required("Description is required field"),
    tenantId:
      userRole === "superadmin"
        ? yup.string().required("Tenant is required field")
        : yup.string(),
  });

  return (
    <Dialog
      disableOverlayClick
      isOpen={isOpen}
      onClose={() => handleCloseModal()}
      overflowY={"auto"}
    >
      <Formik
        innerRef={formikRef}
        initialValues={initialValues}
        validationSchema={validationSchema}
        onSubmit={handleSubmit}
      >
        {({ setFieldValue }) => {
          return (
            <Form>
              <DialogHeader>
                <Text fontWeight={700}>
                  {isAddMode ? "Add General Setting" : "Update General Setting"}
                </Text>
              </DialogHeader>
              <DialogContent>
                <FormWrapper>
                  <Field name="name">
                    {({ field, meta }: FieldProps<string>) => (
                      <Stack spacing="xs">
                        <FormLabel htmlFor="name">Name</FormLabel>
                        <InputGroup inputSize={40}>
                          <InputText
                            type="text"
                            size={40}
                            errors={!!meta.error && meta.touched}
                            {...field}
                          />
                        </InputGroup>
                        {!!meta.error && meta.touched && (
                          <InputMessage variant="error">
                            {meta.error}
                          </InputMessage>
                        )}
                      </Stack>
                    )}
                  </Field>
                  <Field name="value">
                    {({ field, meta }: FieldProps<string>) => (
                      <Stack spacing="xs">
                        <FormLabel htmlFor="value">Value</FormLabel>
                        <InputGroup inputSize={40}>
                          <InputText
                            type="text"
                            size={40}
                            errors={!!meta.error && meta.touched}
                            {...field}
                          />
                        </InputGroup>
                        {!!meta.error && meta.touched && (
                          <InputMessage variant="error">
                            {meta.error}
                          </InputMessage>
                        )}
                      </Stack>
                    )}
                  </Field>
                  {userRole === "superadmin" ? (
                    <GridWrapper>
                      <Field name="category">
                        {({ field, meta }: FieldProps<string>) => (
                          <Stack spacing="xs">
                            <FormLabel htmlFor="category">Category</FormLabel>
                            <InputGroup inputSize={40}>
                              <InputText
                                type="text"
                                size={40}
                                errors={!!meta.error && meta.touched}
                                {...field}
                              />
                            </InputGroup>
                            {!!meta.error && meta.touched && (
                              <InputMessage variant="error">
                                {meta.error}
                              </InputMessage>
                            )}
                          </Stack>
                        )}
                      </Field>

                      <Field name="tenantId">
                        {({ field, meta }: FieldProps<string>) => (
                          <Stack spacing="xs">
                            <FormLabel htmlFor="tenantId">Tenant</FormLabel>
                            <InputGroup inputSize={40}>
                              <InputSelect
                                selectedItem={tenant}
                                width={"100%"}
                                placeholder="Select Tenant"
                                handleSelectedItemChange={({
                                  selectedItem,
                                }) => {
                                  if (selectedItem) {
                                    setTenant(selectedItem);
                                    setFieldValue(
                                      "tenantId",
                                      selectedItem.value
                                    );
                                  }
                                }}
                                itemToString={(item) =>
                                  item ? `${item.label}` : ""
                                }
                                itemValue={(item) =>
                                  item ? `${item.value}` : ""
                                }
                                items={tenantItems}
                                size={"lg"}
                                borderRadius={"6px"}
                                errors={!!meta.error && meta.touched}
                              />
                            </InputGroup>
                            {!!meta.error && meta.touched && (
                              <InputMessage variant="error">
                                {meta.error}
                              </InputMessage>
                            )}
                          </Stack>
                        )}
                      </Field>
                    </GridWrapper>
                  ) : (
                    <Field name="category">
                      {({ field, meta }: FieldProps<string>) => (
                        <Stack spacing="xs">
                          <FormLabel htmlFor="category">Category</FormLabel>
                          <InputGroup inputSize={40}>
                            <InputText
                              type="text"
                              size={40}
                              errors={!!meta.error && meta.touched}
                              {...field}
                            />
                          </InputGroup>
                          {!!meta.error && meta.touched && (
                            <InputMessage variant="error">
                              {meta.error}
                            </InputMessage>
                          )}
                        </Stack>
                      )}
                    </Field>
                  )}
                  <Field name="description">
                    {({ field, meta }: FieldProps<string>) => (
                      <Stack spacing="xs">
                        <FormLabel htmlFor="description">Description</FormLabel>
                        <InputGroup inputSize={40}>
                          <InputText
                            type="text"
                            size={40}
                            errors={!!meta.error && meta.touched}
                            {...field}
                          />
                        </InputGroup>
                        {!!meta.error && meta.touched && (
                          <InputMessage variant="error">
                            {meta.error}
                          </InputMessage>
                        )}
                      </Stack>
                    )}
                  </Field>
                </FormWrapper>
              </DialogContent>
              <DialogFooter>
                <Stack
                  display="flex"
                  flexDirection="row"
                  spacing="xs"
                  flex={1}
                  style={{ gap: 15 }}
                >
                  <Button
                    block
                    variant="outline"
                    onClick={() => handleCloseModal()}
                  >
                    Cancel
                  </Button>
                  <Button
                    type="submit"
                    isLoading={isFormLoading}
                    block
                    variant="primary"
                  >
                    {isAddMode ? "Add" : "Update"}
                  </Button>
                </Stack>
              </DialogFooter>
            </Form>
          );
        }}
      </Formik>
    </Dialog>
  );
};

export default GeneralSettingFormModal;
